import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box
} from "@mui/material";
import { adminUserTypes, hasTechAdminAccess, hasConsultantAccess } from '../../Utils/adminUserTypes';
import "./ActionMenu.css";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockResetIcon from '@mui/icons-material/LockReset';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  DETAILED_VIEW_LABEL,
  STANDARD_VIEW_LABEL,
  DELETE_LABEL,
} from '../../Constants/listRowActions';

const ActionMenu = ({
  anchorEl,
  open,
  onClose,
  actions,
  selectedRow,
  isEdit,
}) => {
  const adminUserType = adminUserTypes();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteConfirm = () => {
    const deleteAction = actions.find((action) => action.label === DELETE_LABEL || action.label === 'Delete');
    if (deleteAction?.onClick) {
      deleteAction.onClick(selectedRow);
    }
    setDeleteDialogOpen(false);
    onClose();
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Helper function to get the appropriate icon
  const getActionIcon = (action) => {
    // If the action has an icon property, use it
    if (action.icon) {
      return action.icon;
    }
    
    // Otherwise, use the predefined icons based on label
    switch (action.label) {
      case "View Document":
        return <VisibilityOutlinedIcon sx={{ color: 'inherit' }} />;
      case "Overview":
      case DETAILED_VIEW_LABEL:
        return <InfoOutlinedIcon sx={{ color: 'inherit' }} />;
      case "Edit":
      case STANDARD_VIEW_LABEL:
        return <EditOutlinedIcon sx={{ color: 'inherit' }} />;
      case "Edit All":
        return <SettingsIcon sx={{ color: 'inherit' }} />;
      case "Delete":
      case DELETE_LABEL:
        return <DeleteOutlineIcon sx={{ color: 'inherit' }} />;
      case "Account":
        return <AccountCircleOutlinedIcon sx={{ color: 'inherit' }} />;
      case "Regenerate":
        return <AutorenewIcon sx={{ color: 'inherit' }} />;
      case "Reset Password":
        return <LockResetIcon sx={{ color: 'inherit' }} />;
      case "Timeline":
        return <TimelineIcon sx={{ color: 'inherit' }} />;
      case "Control List":
        return <ListAltIcon sx={{ color: 'inherit' }} />;
      case "Add Users":
        return <PersonAddIcon sx={{ color: 'inherit' }} />;
      case "Upgrade/Renew":
        return <AutorenewIcon sx={{ color: 'inherit' }} />;
      default:
        return null;
    }
  };

  // Don't render menu if no row is selected
  if (!selectedRow) {
    return null;
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: '200px',
            borderRadius: '8px',
            mt: 1,
            '& .MuiList-root': {
              padding: '4px',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {actions?.map((action, index) => {
          // Skip actions that are meant for the global dropdown menu
          if (action.showInDropdown) {
            return null;
          }

          // Check if action has a condition function and evaluate it
          if (action.condition && typeof action.condition === 'function') {
            // Only evaluate condition if selectedRow exists
            if (!selectedRow || !action.condition(selectedRow)) {
              if (selectedRow) {
                console.log(`Action "${action.label}" hidden due to condition check`, {
                  action: action.label,
                  row: selectedRow,
                  conditionResult: action.condition(selectedRow)
                });
              }
              return null;
            }
          }

          // Check if Edit or Delete should be enabled based on adminUserType
          if (!adminUserType) {
            if ((action.label === 'Edit' || action.label === STANDARD_VIEW_LABEL || action.label === DELETE_LABEL || action.label === 'Delete') && selectedRow?.is_system_grp) {
              return null;
            }
          }

          // Hide the Edit option if the status is "Approved"
          if ((action.label === 'Edit' || action.label === STANDARD_VIEW_LABEL) && selectedRow?.status === 'Approved') {
            return null;
          }

          // Hide View Document option if user is not tech admin
          if (action.label === "View Document" && !hasTechAdminAccess()) {
            return null;
          }

          return (
            <MenuItem
              key={index}
              onClick={() => {
                if (action.label === DELETE_LABEL || action.label === 'Delete') {
                  setDeleteDialogOpen(true);
                } else {
                  action.onClick(selectedRow);
                  onClose();
                }
              }}
              sx={{ 
                borderRadius: '6px',
                padding: '8px 16px',
                margin: '1px 0',
                color: (action.label === DELETE_LABEL || action.label === 'Delete') ? "#d32f2f" : "#1e2737",
                '&:hover': {
                  backgroundColor: (action.label === DELETE_LABEL || action.label === 'Delete') ? '#ffebee' : '#f5f5f5',
                },
                '&:last-child': {
                  marginBottom: 0,
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: '36px'
                },
                '& .MuiTypography-root': {
                  fontSize: '14px',
                  fontWeight: 500
                }
              }}
            >
              <ListItemIcon>
                {typeof action.icon === 'function' ? action.icon(selectedRow) : getActionIcon(action)}
              </ListItemIcon>
              <Typography variant="inherit">
                {typeof action.label === 'function' ? action.label(selectedRow) : action.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ 
          pr: 6,
          pb: 1,
          '& .MuiTypography-root': {
            fontSize: '20px',
            fontWeight: 600
          }
        }}>
          Confirm Delete
          <IconButton
            aria-label="close"
            onClick={handleDeleteCancel}
            sx={{
              position: 'absolute',
              right: 16,
              top: 12,
              color: '#666'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#666' }}>
            Are you sure you want to delete this item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ 
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#c62828'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionMenu;
